import PptxGenJS from "pptxgenjs";

export type PptxInstance = InstanceType<typeof PptxGenJS>;
export type PptxSlide = ReturnType<PptxInstance["addSlide"]>;
export type PptxShapeName = Parameters<PptxSlide["addShape"]>[0];
export type PptxShapeProps = NonNullable<Parameters<PptxSlide["addShape"]>[1]>;

export const SLIDE_W = 13.333;
export const SLIDE_H = 7.5;
export const TITLE_H = 0.58;

export const FONTS = {
  title: "Garamond",
  body: "Segoe UI",
} as const;

export function hexNoHash(color: string): string {
  return color.replace(/^#/, "");
}

export const COLORS = {
  navy: "0D2137",
  navyLight: "173A5E",
  navyMid: "12304F",
  navyMidLight: "1B4570",
  white: "FFFFFF",
  line: "C5CAD3",
  hub: "1A6B5C",
  totalRow: "E8EAED",
  textDark: "182737",
  muted: "5A6570",
  barTrack: "E8ECF1",
  tableLine: "D9DEE5",
} as const;

type PptxShapesApi = {
  RECTANGLE: PptxShapeName;
  ROUNDED_RECTANGLE: PptxShapeName;
  CUSTOM_GEOMETRY: PptxShapeName;
};

export function createPresentation(): PptxInstance {
  const maybeCtor = PptxGenJS as unknown as
    | (new () => PptxInstance)
    | { default: new () => PptxInstance };
  const Ctor =
    typeof maybeCtor === "function" ? maybeCtor : maybeCtor.default;
  return new Ctor();
}

export function getShapes(pptx: PptxInstance): PptxShapesApi {
  const shapes = (pptx as PptxInstance & { shapes: Record<string, string> })
    .shapes;
  return {
    RECTANGLE: shapes.RECTANGLE as PptxShapeName,
    ROUNDED_RECTANGLE: shapes.ROUNDED_RECTANGLE as PptxShapeName,
    CUSTOM_GEOMETRY: shapes.CUSTOM_GEOMETRY as PptxShapeName,
  };
}

export function addShape(
  slide: PptxSlide,
  shape: PptxShapeName,
  options: PptxShapeProps,
): void {
  const draw = slide.addShape.bind(slide) as (
    shapeName: PptxShapeName,
    opts?: PptxShapeProps,
  ) => void;
  draw(shape, options);
}

function mixHex(from: string, to: string, ratio: number): string {
  const a = parseInt(hexNoHash(from), 16);
  const b = parseInt(hexNoHash(to), 16);
  const channel = (shift: number) => {
    const start = (a >> shift) & 0xff;
    const end = (b >> shift) & 0xff;
    return Math.round(start + (end - start) * ratio)
      .toString(16)
      .padStart(2, "0");
  };
  return `${channel(16)}${channel(8)}${channel(0)}`.toUpperCase();
}

export type GradientPanelOptions = {
  x: number;
  y: number;
  w: number;
  h: number;
  from: string;
  to: string;
  direction?: "vertical" | "horizontal";
  rectRadius?: number;
  bands?: number;
};

/**
 * PowerPoint gradient fills are not exposed by PptxGenJS, so a gradient is
 * approximated with interpolated bands. Each band overhangs the next by the
 * corner radius so the panel keeps a rounded silhouette without visible notches.
 */
export function addGradientPanel(
  pptx: PptxInstance,
  slide: PptxSlide,
  options: GradientPanelOptions,
): void {
  const { RECTANGLE, ROUNDED_RECTANGLE } = getShapes(pptx);
  const horizontal = options.direction === "horizontal";
  const radius = options.rectRadius ?? 0;
  const shape = radius > 0 ? ROUNDED_RECTANGLE : RECTANGLE;
  const bandCount = Math.max(2, options.bands ?? 14);
  const bandSpan = (horizontal ? options.w : options.h) / bandCount;
  // Bands overlap slightly so PowerPoint never renders a hairline seam between them.
  const overhang = Math.min(radius > 0 ? radius : 0.02, bandSpan);

  for (let i = 0; i < bandCount; i += 1) {
    const color = mixHex(options.from, options.to, i / (bandCount - 1));
    const offset = i * bandSpan;
    const span = i === bandCount - 1 ? bandSpan : bandSpan + overhang;
    addShape(slide, shape, {
      x: horizontal ? options.x + offset : options.x,
      y: horizontal ? options.y : options.y + offset,
      w: horizontal ? span : options.w,
      h: horizontal ? options.h : span,
      fill: { color },
      line: { color, width: 0 },
      ...(radius > 0 ? { rectRadius: radius } : {}),
    });
  }
}

export function formatGbp(value: number): string {
  return value.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export function formatGbpMm(value: number): string {
  const mm = value / 1_000_000;
  return `£${mm.toLocaleString("en-GB", {
    maximumFractionDigits: 0,
  })}MM`;
}

export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

let logoDataUriPromise: Promise<string | null> | null = null;
let logoDataUriOverride: string | null | undefined;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  // Prefer browser btoa; fall back for Node smoke tests.
  if (typeof btoa === "function") return btoa(binary);
  return Buffer.from(bytes).toString("base64");
}

/** Browser path: fetch the bundled logo asset once and cache a data URI. */
export function loadTenfiftyLogoDataUri(): Promise<string | null> {
  if (logoDataUriOverride !== undefined) {
    return Promise.resolve(logoDataUriOverride);
  }
  if (!logoDataUriPromise) {
    logoDataUriPromise = import("../../assets/tenfifty-logo.png")
      .then((mod) => {
        const url =
          typeof mod.default === "string" ? mod.default : String(mod.default);
        return fetch(url);
      })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load TenFifty logo");
        return response.arrayBuffer();
      })
      .then((buffer) => {
        const base64 = bytesToBase64(new Uint8Array(buffer));
        return `data:image/png;base64,${base64}`;
      })
      .catch((error) => {
        console.warn("TenFifty logo unavailable for PowerPoint export:", error);
        return null;
      });
  }
  return logoDataUriPromise;
}

/** Allow Node smoke tests to inject a logo without Vite asset resolution. */
export function setTenfiftyLogoDataUriOverride(
  dataUri: string | null,
): void {
  logoDataUriOverride = dataUri;
  logoDataUriPromise = Promise.resolve(dataUri);
}

const TABLE_HEADER_RULE_H = 0.03;

/** Row dividers only: bottom edge on every cell, no vertical rules. */
export const TABLE_ROW_BORDER: [
  PptxGenJS.BorderProps,
  PptxGenJS.BorderProps,
  PptxGenJS.BorderProps,
  PptxGenJS.BorderProps,
] = [
  { type: "none" },
  { type: "none" },
  { type: "solid", pt: 0.5, color: COLORS.tableLine },
  { type: "none" },
];

export type TableHeaderRuleOptions = {
  x: number;
  y: number;
  w: number;
  /** Height of the header row, used to place the rule beneath it. */
  headerRowH: number;
};

/**
 * Heavy rule separating the header row from the body. Call after `addTable`
 * so it sits above the cell fills.
 */
export function addTableHeaderRule(
  pptx: PptxInstance,
  slide: PptxSlide,
  options: TableHeaderRuleOptions,
): void {
  const { RECTANGLE } = getShapes(pptx);
  addShape(slide, RECTANGLE, {
    x: options.x,
    y: options.y + options.headerRowH - TABLE_HEADER_RULE_H / 2,
    w: options.w,
    h: TABLE_HEADER_RULE_H,
    fill: { color: COLORS.textDark },
    line: { color: COLORS.textDark, width: 0 },
  });
}

export type TitleBarOptions = {
  title: string;
  logoDataUri?: string | null;
};

const LOGO_W = 0.7;
const LOGO_H = 0.44;

export function addTitleBar(
  pptx: PptxInstance,
  slide: PptxSlide,
  options: TitleBarOptions,
): void {
  addGradientPanel(pptx, slide, {
    x: 0,
    y: 0,
    w: SLIDE_W,
    h: TITLE_H,
    from: COLORS.navy,
    to: COLORS.navyLight,
    direction: "horizontal",
    bands: 16,
  });

  const titleRightPad = options.logoDataUri ? LOGO_W + 0.35 : 0.22;
  slide.addText(options.title, {
    x: 0.22,
    y: 0,
    w: SLIDE_W - 0.22 - titleRightPad,
    h: TITLE_H,
    fontFace: FONTS.title,
    fontSize: 22,
    color: COLORS.white,
    bold: false,
    align: "left",
    valign: "middle",
    margin: 0,
  });

  if (options.logoDataUri) {
    slide.addImage({
      data: options.logoDataUri,
      x: SLIDE_W - LOGO_W - 0.18,
      y: (TITLE_H - LOGO_H) / 2,
      w: LOGO_W,
      h: LOGO_H,
    });
  }
}

export function configureDeck(pptx: PptxInstance): void {
  pptx.defineLayout({ name: "TENFIFTY_PPT", width: SLIDE_W, height: SLIDE_H });
  pptx.layout = "TENFIFTY_PPT";
  pptx.author = "10Fifty Genesis";
  pptx.title = "TenFifty PPT Summary";
  pptx.subject = "Pipeline Analysis";
  pptx.theme = { headFontFace: FONTS.title, bodyFontFace: FONTS.body };
}
